import * as THREE from 'three';

// 用程式生成真正立體的蓮花模型（非平面貼圖）：每片花瓣是有厚度、有彎曲弧度的
// 曲面幾何體，多層環狀排列組成整朵花，任何角度旋轉觀察都是正確的立體形狀。

function shadeColor(hex, percent) {
    const num = parseInt(hex.replace('#', ''), 16);
    let r = (num >> 16) + percent;
    let g = ((num >> 8) & 0x00ff) + percent;
    let b = (num & 0x0000ff) + percent;
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));
    return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
}

// 花瓣的 2D 輪廓（柳葉狀，底部窄、中段最寬、瓣尖尖細），單位化座標（長度 1、寬度依 widthRatio 給定）
function buildPetalShape(widthRatio) {
    const shape = new THREE.Shape();
    const w = widthRatio / 2;
    shape.moveTo(0, 0);
    shape.bezierCurveTo(w * 1.05, 0.08, w, 0.42, w * 0.7, 0.75);
    shape.quadraticCurveTo(w * 0.4, 0.98, 0, 1);
    shape.quadraticCurveTo(-w * 0.4, 0.98, -w * 0.7, 0.75);
    shape.bezierCurveTo(-w, 0.42, -w * 1.05, 0.08, 0, 0);
    return shape;
}

// 把花瓣的平面 mesh 沿著長軸方向彎出弧度（往內凹，像真實花瓣受力自然形成的曲面），
// 並在寬度方向也加一點內凹（避免整片死平，中軸微凸、兩側微捲）
function bendGeometry(geometry, curveAmount, tipCurl) {
    const position = geometry.attributes.position;
    for (let i = 0; i < position.count; i++) {
        const x = position.getX(i);
        const y = position.getY(i);
        // 沿 y（花瓣長度方向）彎曲：y 越大（越接近瓣尖）往 z 方向凹陷越多
        const bendZ = -curveAmount * y * y;
        // 沿 x（花瓣寬度方向）微捲：邊緣往上翹一點，中軸最低，模擬花瓣弧面
        const curlZ = tipCurl * x * x * y;
        position.setZ(i, position.getZ(i) + bendZ + curlZ);
    }
    geometry.computeVertexNormals();
    return geometry;
}

// 建立單片花瓣的 3D 幾何體：擠出一點厚度＋彎曲弧度
function createPetalGeometry({ length, widthRatio, thickness, curveAmount, tipCurl, segments = 8 }) {
    const shape = buildPetalShape(widthRatio);
    const geometry = new THREE.ExtrudeGeometry(shape, {
        depth: thickness,
        bevelEnabled: false,
        curveSegments: segments,
        steps: segments,
    });
    // ExtrudeGeometry 預設沿 z 擠出，這裡座標系是 shape 的 x=寬度, y=長度(0=花萼端,1=瓣尖)
    geometry.scale(length, length, 1);
    geometry.translate(0, 0, -thickness / 2);
    bendGeometry(geometry, curveAmount, tipCurl);
    return geometry;
}

/**
 * 建立一整朵立體蓮花的 THREE.Group。每片花瓣都是有厚度、有弧度的獨立幾何體，
 * 分層環狀排列（外層寬扁下垂、內層窄直立包住花心），任何角度旋轉都是正確的立體花朵。
 */
export function createLotusModel(color = '#F5C97B') {
    const group = new THREE.Group();

    const layers = [
        // 最外層：完全綻放、花瓣最長，接近水平甚至略微外翻下垂、色最深，撐開花朵最寬的輪廓
        { count: 8, length: 0.84, widthRatio: 0.62, tiltDeg: 100, curveAmount: 0.32, tipCurl: 0.22, colorShift: -20 },
        { count: 8, length: 0.8, widthRatio: 0.56, tiltDeg: 78, curveAmount: 0.28, tipCurl: 0.2, colorShift: -10 },
        { count: 6, length: 0.66, widthRatio: 0.5, tiltDeg: 55, curveAmount: 0.24, tipCurl: 0.18, colorShift: 0 },
        // 最內層：花瓣最短，直立包住花心
        { count: 5, length: 0.5, widthRatio: 0.42, tiltDeg: 32, curveAmount: 0.2, tipCurl: 0.14, colorShift: 14 },
    ];

    layers.forEach((layer, layerIndex) => {
        const fillColor = shadeColor(color, layer.colorShift);
        const material = new THREE.MeshStandardMaterial({
            color: fillColor,
            // 加一點自發光等於原色，確保花瓣暗部/背光面不會被場景光照吃成死暗、
            // 顏色不會看起來比選的色票淡很多，同時仍保留光照帶來的立體層次
            emissive: fillColor,
            emissiveIntensity: 0.45,
            roughness: 0.55,
            metalness: 0.05,
            side: THREE.DoubleSide,
        });

        const angleOffset = (layerIndex * Math.PI) / (layer.count * 1.7);
        for (let i = 0; i < layer.count; i++) {
            const geometry = createPetalGeometry({
                length: layer.length,
                widthRatio: layer.widthRatio,
                thickness: 0.02,
                curveAmount: layer.curveAmount,
                tipCurl: layer.tipCurl,
            });
            const mesh = new THREE.Mesh(geometry, material);

            const angle = (Math.PI * 2 * i) / layer.count + angleOffset;
            const tilt = (layer.tiltDeg * Math.PI) / 180;

            // 花瓣基座先繞 X 軸往外傾倒 tilt 角度（讓花瓣從花心向外展開/下垂），
            // 再繞 Y 軸旋轉到環狀對應的角度位置
            mesh.rotation.x = tilt;
            mesh.position.set(0, 0, 0);

            const pivot = new THREE.Group();
            pivot.add(mesh);
            pivot.rotation.y = angle;
            group.add(pivot);
        }
    });

    // 花蕊：花心周圍一圈細短雄蕊（用細長 CylinderGeometry 模擬）
    const stamenMaterial = new THREE.MeshStandardMaterial({ color: shadeColor(color, 35), roughness: 0.5 });
    const stamenTipMaterial = new THREE.MeshStandardMaterial({ color: '#E8B84B', roughness: 0.4 });
    const stamenCount = 10;
    for (let i = 0; i < stamenCount; i++) {
        const a = (Math.PI * 2 * i) / stamenCount;
        const r = 0.1;
        const stamenGeo = new THREE.CylinderGeometry(0.004, 0.006, 0.16, 4);
        const stamen = new THREE.Mesh(stamenGeo, stamenMaterial);
        stamen.position.set(Math.cos(a) * r, 0.08, Math.sin(a) * r);
        stamen.rotation.x = Math.PI / 2.3;
        stamen.rotation.z = a;
        group.add(stamen);

        const tipGeo = new THREE.SphereGeometry(0.012, 6, 6);
        const tip = new THREE.Mesh(tipGeo, stamenTipMaterial);
        tip.position.set(Math.cos(a) * r * 1.5, 0.14, Math.sin(a) * r * 1.5);
        group.add(tip);
    }

    // 花心（蓮蓬）
    const podGeo = new THREE.CylinderGeometry(0.09, 0.11, 0.09, 12);
    const podMaterial = new THREE.MeshStandardMaterial({ color: shadeColor(color, 42), roughness: 0.6 });
    const pod = new THREE.Mesh(podGeo, podMaterial);
    pod.position.y = 0.06;
    group.add(pod);

    return group;
}

/**
 * 收集一個 Group 內所有幾何體與材質，供外部統一 dispose 釋放記憶體。
 */
export function collectDisposables(group) {
    const geometries = [];
    const materials = new Set();
    group.traverse((obj) => {
        if (obj.geometry) geometries.push(obj.geometry);
        if (obj.material) materials.add(obj.material);
    });
    return { geometries, materials: Array.from(materials) };
}
